import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormControl, AbstractControl } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { isNullOrUndefined } from '@uzurv/core';
import { CheckboxModule } from '@uzurv/afp/checkbox';
import { RadioGroupModule } from '@uzurv/afp/radio-group/module';

export interface CheckboxGroup {
  label: string;
  description: string;
  isNewForm: boolean;
  options: Array<CheckboxOption>;
}

export interface CheckboxOption {
  label: string;
  name: string;
  checked?: boolean;
  additionalOptions?: Array<AdditionalOption>;
}

export interface AdditionalOption {
  name: string;
  label: string;
  type: 'radio' | 'checkbox';
  showIfOptionValue?: Array<{ name: string; value: string }>;
  options: Array<AdditionalOptionValue>;
  value?: string;
}

export interface AdditionalOptionValue {
  value: string;
  label: string;
  checked?: boolean;
}

export interface CheckboxGroupOutput {
  dirty: boolean;
  valid: boolean;
  value: { options: Array<CheckboxOption> };
}

@Component({
  selector: 'app-checkbox-form-group',
  standalone: true,
  templateUrl: './checkbox-form-group.html',
  styleUrls: ['./checkbox-form-group.scss'],
  imports: [CommonModule, ReactiveFormsModule, RadioGroupModule, CheckboxModule]
})
export class CheckboxFormGroup extends BaseComponent implements OnInit, OnDestroy {
  @Input() checkboxGroup: CheckboxGroup;
  @Input() showValidationErrors = false;
  @Input() addNoneOption = true;
  @Input() disableFormSelection = true;

  @Output() formStatusChanged = new EventEmitter<CheckboxGroupOutput>();

  checkoutGroupForm: FormGroup<{ options: FormArray<FormGroup> }>;
  private lastOptionToggledIndex: number | null = null;

  constructor(private formBuilder: FormBuilder) {
    super();
  }

  get optionsFormArray(): FormArray {
    return this.checkoutGroupForm.get('options') as FormArray;
  }

  ngOnInit(): void {
    const options = [...this.checkboxGroup.options];
    if (this.addNoneOption) {
      options.push({ label: 'None', name: 'none', checked: false });
    }

    const formGroupArray = this.formBuilder.array(options.map(option => this.createOptionGroup(option)));
    this.checkoutGroupForm = this.formBuilder.group(
      { options: formGroupArray },
      { validators: this.addNoneOption ? this.addNoteOptionValidator.bind(this) : null }
    ) as FormGroup<{ options: FormArray<FormGroup> }>;

    this.optionsFormArray.controls.forEach((group, index) => {
      group.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.lastOptionToggledIndex = index;
        this.updateFormData();
      });
    });

    this.checkoutGroupForm.valueChanges.pipe(debounceTime(100), takeUntil(this.destroyed$)).subscribe(() => {
      this.updateFormValidation();
      this.emitFormStatus();
    });

    this.updateFormData();
    this.updateFormValidation();
    this.emitFormStatus();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getOptionGroup(index: number): FormGroup {
    return this.optionsFormArray.at(index) as FormGroup;
  }

  getAdditionalOptionsGroup(optionGroup: AbstractControl): Array<FormGroup> {
    const formArray = optionGroup.get('additionalOptions') as FormArray;
    return formArray?.controls as Array<FormGroup>;
  }

  private addNoteOptionValidator(form: AbstractControl) {
    const options = (form.get('options') as FormArray).controls;
    const hasSelection = options.some(ctrl => ctrl.get('checked')?.value);

    const noneOption = options.find(ctrl => ctrl.get('name')?.value === 'none');
    const noneChecked = noneOption?.get('checked')?.value;

    return hasSelection || noneChecked ? null : { invalidProfile: true };
  }

  private createOptionGroup(option: CheckboxOption): FormGroup {
    const additionalOptions = this.formBuilder.array(
      (option.additionalOptions || []).map(opt => this.createAdditionalOptionGroup(opt, option.checked))
    );

    return this.formBuilder.group({
      label: option.label,
      name: option.name,
      checked: [option.checked || false],
      additionalOptions
    });
  }

  private createAdditionalOptionGroup(option: AdditionalOption, parentChecked?: boolean): FormGroup {
    const visible = !option.showIfOptionValue || option.showIfOptionValue.length === 0;

    if (option.type === 'radio') {
      const selected = option.options.find(o => o.checked);
      const control = new FormControl(selected?.value || '', parentChecked ? Validators.required : []);
      return this.formBuilder.group({
        type: option.type,
        label: option.label,
        name: option.name,
        showIfOptionValue: new FormControl(option.showIfOptionValue || []),
        visible: new FormControl(visible),
        value: control,
        options: [option.options.map(option => ({ ...option }))]
      });
    }

    const optionsArray: AdditionalOptionValue[] = option.options.map(opt => ({
      value: opt.value,
      label: opt.label,
      checked: !!opt.checked
    }));

    return this.formBuilder.group({
      type: option.type,
      label: option.label,
      name: option.name,
      showIfOptionValue: new FormControl(option.showIfOptionValue || []),
      visible: new FormControl(visible),
      value: new FormControl(optionsArray),
      options: [optionsArray]
    });
  }

  private updateFormData(): void {
    const noneGroup = this.addNoneOption ? this.optionsFormArray.controls.find(ctrl => ctrl.get('name')?.value === 'none') : null;
    const noneIndex = this.optionsFormArray.controls.indexOf(noneGroup!);
    const noneChecked = noneGroup?.get('checked')?.value ?? false;

    // None Checked - Reset Others
    if (this.lastOptionToggledIndex === noneIndex && noneChecked) {
      this.optionsFormArray.controls.forEach((ctrl, i) => {
        if (i !== noneIndex) {
          ctrl.get('checked')?.setValue(false, { emitEvent: false });
          this.resetAdditionalOptions(ctrl);
        }
      });
    }

    // Others Checked - Reset None
    if (this.lastOptionToggledIndex !== noneIndex && noneChecked) {
      noneGroup?.get('checked')?.setValue(false, { emitEvent: false });
    }

    // isNewForm AND No options are checked - None unchecked
    // !isNewForm AND No options are checked - None checked
    if (!this.checkboxGroup.isNewForm && !noneChecked) {
      const allUnchecked = this.optionsFormArray.controls.every(ctrl => !ctrl.get('checked')?.value);
      if (allUnchecked) {
        noneGroup?.get('checked')?.setValue(true, { emitEvent: false });
        this.resetAdditionalOptions(noneGroup!);
      }
    }

    // Cascade things
    this.optionsFormArray.controls.forEach(group => {
      const isChecked = group.get('checked')?.value;
      const additionalOptions = group.get('additionalOptions') as FormArray;

      // Show or Hide Additional Options
      additionalOptions.controls.forEach(optionCtrl => {
        const showIfOption = optionCtrl.get('showIfOptionValue')?.value;
        const type = optionCtrl.get('type')?.value;
        const value = optionCtrl.get('value');
        const visibleByDefault = !showIfOption || showIfOption.length === 0; // No showIfOptionValue means visible by default

        // Show Additional Options
        let shouldBeVisible = isChecked && visibleByDefault;
        if (isChecked && showIfOption?.length) {
          // If time.. update to add config to choose some or every condition
          shouldBeVisible = showIfOption.some(condition => {
            const matchingCtrl = additionalOptions.controls.find(
              c => c.get('name')?.value === condition.name && c.get('value')?.value === condition.value
            );
            return !isNullOrUndefined(matchingCtrl);
          });
        }

        const currentVisible = optionCtrl.get('visible')?.value;
        const becomingHidden = currentVisible && !shouldBeVisible;

        optionCtrl.get('visible')?.setValue(shouldBeVisible, { emitEvent: false });

        // Hide Additional Options
        if (becomingHidden) {
          if (type === 'radio') {
            value?.reset('', { emitEvent: false });
          } else if (value instanceof FormGroup) {
            Object.values(value.controls).forEach(control => control.setValue(false, { emitEvent: false }));
          }
        }
      });
    });

    // Updates DisableFormSelection, if needed
    if (this.disableFormSelection) {
      this.optionsFormArray.controls.forEach(group => {
        group.get('checked')?.disable({ emitEvent: false });
        if (group.get('additionalOptions')) {
          const additionalOptions = group.get('additionalOptions') as FormArray;
          additionalOptions.controls.forEach(ctrl => {
            ctrl.get('visible')?.disable({ emitEvent: false });
            if (ctrl.get('value') instanceof FormGroup) {
              Object.values((ctrl.get('value') as FormGroup).controls).forEach(control => control.disable({ emitEvent: false }));
            } else {
              ctrl.get('value')?.disable({ emitEvent: false });
            }
          });
        }
      });
    }

    this.lastOptionToggledIndex = null;
  }

  private updateFormValidation(): void {
    this.optionsFormArray.controls.forEach(group => {
      const isChecked = group.get('checked')?.value;
      const additionalOptions = group.get('additionalOptions') as FormArray;

      additionalOptions.controls.forEach(ctrl => {
        const type = ctrl.get('type')?.value;
        const value = ctrl.get('value');
        const isVisible = ctrl.get('visible')?.value;

        if (type === 'radio') {
          if (isChecked && isVisible) {
            value?.setValidators(Validators.required);
          } else {
            value?.clearValidators();
            value?.reset('', { emitEvent: false });
          }
        } else if (value instanceof FormGroup) {
          Object.values(value.controls).forEach(control =>
            control.setValue(isChecked && isVisible ? control.value : false, { emitEvent: false })
          );
        }

        value?.updateValueAndValidity({ emitEvent: false });
      });
    });

    this.checkoutGroupForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  private emitFormStatus(): void {
    const values = this.checkoutGroupForm.value.options;
    const filtered = values.filter((_: any, index: number) => this.getOptionGroup(index).get('label')?.value !== 'None');
    this.formStatusChanged.emit({
      dirty: this.checkoutGroupForm.dirty,
      valid: this.checkoutGroupForm.valid,
      value: { options: filtered }
    });
  }

  private resetAdditionalOptions(group: AbstractControl): void {
    const additional = group.get('additionalOptions') as FormArray;
    additional.controls.forEach(ctrl => {
      const type = ctrl.get('type')?.value;
      const value = ctrl.get('value');
      ctrl.get('visible')?.setValue(false, { emitEvent: false });

      if (type === 'radio') {
        value?.reset('', { emitEvent: false });
      } else if (value instanceof FormGroup) {
        Object.values(value.controls).forEach(control => control.setValue(false, { emitEvent: false }));
      }
    });
  }
}
